import {
  CallbackFunction,
  ChangeDetector,
  DcuplListOptions,
  IndicesController,
  ListItem,
  RawItem,
  trigger,
} from '@dcupl/common';
import { Catalog } from '../catalog';
import { DcuplModel } from '../model-parser';

import { DcuplApi } from '../dcupl/dcupl-api';
import { cloneDeep } from 'lodash-es';

export class DcuplList<ListType extends RawItem = RawItem> {
  private options!: DcuplListOptions;
  public key!: string;
  public modelKey!: string;
  public catalog!: Catalog<ListType & ListItem>;

  constructor(
    private dcuplApi: DcuplApi,
    private cdRef: ChangeDetector,
  ) {}

  private setup(options: DcuplListOptions): void {
    /**
     * 1) Get the corresponding model
     */
    const model = this.dcuplApi['modelParser'].models.get(options.modelKey) as
      | DcuplModel<ListType & ListItem>
      | undefined;

    if (!model) {
      throw new Error(`Model with key ${options.modelKey} not found`);
    }

    /**
     * 2) Set data based on listItemKeys or query
     */
    const listItemKeys = this.getListItemKeys(options);

    const data: Map<string, ListType & ListItem> = this.getRelevantData(model, listItemKeys);

    /**
     * 3) Create indicesController depdending on listItemKys
     */

    const indicesController = this.initIndicesController(listItemKeys, model, data);

    /**
     * 4) Create catalog
     */
    if (!this.catalog) {
      this.catalog = new Catalog<ListType & ListItem>(this.dcuplApi, this.cdRef, indicesController);
      this.catalog['init'](this.modelKey, model, data);
    } else {
      this.catalog['filter'].update(model, data, indicesController);
    }
  }

  private initIndicesController(
    listItemKeys: string[] | undefined,
    model: DcuplModel<ListType & ListItem>,
    data: Map<string, ListType & ListItem>,
  ): IndicesController {
    let indicesController: IndicesController | undefined;
    if (listItemKeys) {
      indicesController = new IndicesController();
      for (const key of model.indicesController['indexMap'].keys()) {
        indicesController.getOrCreateIndex(key, data);
      }
    } else {
      indicesController = model.indicesController;
    }

    return indicesController;
  }

  private getListItemKeys(options: DcuplListOptions): string[] | undefined {
    let listItemKeys: string[] | undefined;
    if (options.listItemKeys) {
      listItemKeys = options.listItemKeys;
    } else if (options.query) {
      const items = this.dcuplApi.query.execute(options.query);
      listItemKeys = items.map((item) => item.key);
    }

    return listItemKeys;
  }

  private getRelevantData(
    model: DcuplModel<ListType & ListItem>,
    listItemKeys: string[] | undefined,
  ): Map<string, ListType & ListItem> {
    const modelData = model.getData();

    if (listItemKeys) {
      return this.initFromItemKeys(modelData, listItemKeys);
    } else {
      return modelData;
    }
  }

  // private initCdRef(options: DcuplListOptions): void {
  //   const rootCdRef = this.dcuplApi['cdRef'];
  //   this.cdRef = new ChangeDetector();
  //   this.cdRef._init({
  //     cdRefKey: options.listKey!,
  //     parentCd: rootCdRef,
  //     dcuplInitOptions: this.dcuplApi.initOptions,
  //     analyticsController: this.dcuplApi['analyticsController'],
  //   });
  // }

  // @trigger({ scope: 'global', name: 'list:create', type: 'list_created' })
  protected init(_initialOptions: DcuplListOptions): void {
    /**
     * Initialize default options
     */

    this.options = cloneDeep(_initialOptions);
    this.key = this.options.listKey!;
    this.modelKey = this.options.modelKey;
    // this.initCdRef(this.options);
    this.setup(this.options);
  }

  private initFromItemKeys(
    modelData: Map<string, ListType & ListItem>,
    itemKeys: string[],
  ): Map<string, ListType & ListItem> {
    const data = new Map();
    itemKeys.forEach((key) => {
      if (modelData.has(key)) {
        const item = modelData.get(key) as ListType & ListItem;
        if (item) {
          data.set(key, item);
        }
      }
    });
    return data;
  }

  public destroy(): void {
    try {
      this.dcuplApi.lists.destroy({ listKey: this.key });
    } catch (err) {}
  }

  @trigger({
    scope: 'list',
    type: 'list_destroyed',
    connectMessage: true,
  })
  protected _destroy() {
    this.catalog['filter'].cdRef?.destroy();
  }

  @trigger({
    scope: 'list',
    type: 'list_updated',
    action: 'update',
    connectMessage: true,
  })
  public update(options?: { reprocessData?: boolean }): void {
    if ((options && options.reprocessData) || !options) {
      this.setup(this.options);
    }
  }

  public on(cb: CallbackFunction): () => void {
    return this.cdRef.on(cb);
  }
}
